import { Component } from '@theme/component';

/**
 * Seasonal mosaic: a set of season tabs that each swap in a puzzle/mosaic
 * grid of dress images below them.
 *
 * The tab buttons are NOT server-rendered. Horizon's `section.blocks` proxy
 * exposes each `content_for`-rendered block's `id`, but reports its `type` as
 * `@theme` and exposes none of its `settings`, so the parent section cannot
 * read each `_season-tab`'s `label` in order to build the tablist in Liquid.
 * Each panel therefore publishes its own label on `data-season-label`, and
 * this component builds the matching buttons from the panels on connect.
 *
 * Tabs are queried from the DOM rather than read off `this.refs`, because the
 * buttons are created here and the base class's ref collection is not
 * guaranteed to have observed them yet at that point.
 *
 * Activation (`on:click`) and keyboard navigation (`on:keydown`) are set as
 * attributes on the generated buttons and dispatched by the base `Component`
 * class's single document-level delegated listener (see `@theme/component`),
 * so this class adds no listeners of its own and has nothing beyond the base
 * class's teardown to release.
 *
 * @typedef {object} SeasonalMosaicRefs
 * @property {HTMLElement[]} [panels] - The mosaic panels, one per season, in DOM order.
 *
 * @extends {Component<SeasonalMosaicRefs>}
 */
export class SeasonalMosaicComponent extends Component {
  /**
   * Builds the tab buttons from the server-rendered panels, then lets the base
   * class finish connecting.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#showCurrentSeason();
    this.#buildTabs();
  }

  /**
   * Activates the season whose tab was clicked.
   *
   * @param {Event} event - The click event from the `on:click="/selectSeason"` binding.
   */
  selectSeason(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.closest('[role="tab"]');
    if (!tab) return;

    const index = this.#tabs().indexOf(/** @type {HTMLElement} */ (tab));
    if (index === -1) return;

    this.#activate(index);
  }

  /**
   * Roving-tabindex keyboard navigation for the tablist. ArrowLeft/ArrowRight
   * and Home/End move focus between tabs without changing the active panel
   * (the manual-activation ARIA tabs pattern); Enter/Space activation is
   * handled for free by the native `<button>` via `on:click`.
   *
   * @param {KeyboardEvent} event - The keydown event from the `on:keydown="/handleTabKeydown"` binding.
   */
  handleTabKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tabs = this.#tabs();
    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (target.closest('[role="tab"]')));
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#focusTab(nextIndex);
  }

  /**
   * The generated tab buttons add no listeners of their own, and this
   * component owns no observers or timers beyond the base class's, which
   * `super.disconnectedCallback()` releases. Declared explicitly so that
   * stays true on purpose rather than by accident.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * @returns {HTMLElement[]} The tab buttons, in DOM order.
   */
  #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /**
   * @returns {HTMLElement[]} The season panels, in DOM order.
   */
  #panels() {
    const { panels } = this.refs;
    if (panels?.length) return panels;
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.mosaic-panel')]);
  }

  /**
   * Shows the panel tagged (via the block's `season` setting, published on
   * `data-season`) with the visitor's current meteorological season: Dec to
   * Feb winter, Mar to May spring, Jun to Aug summer, Sep to Nov autumn.
   * Done client-side so the visitor's own date is used and a cached page is
   * never stuck on a stale season. If no panel matches, the server-rendered
   * first panel stays visible.
   */
  #showCurrentSeason() {
    const month = new Date().getMonth();
    const season = ['winter', 'spring', 'summer', 'autumn'][Math.floor(((month + 1) % 12) / 3)];

    const panels = this.#panels();
    const match = panels.find((panel) => panel.dataset.season === season);
    if (!match) return;

    for (const panel of panels) {
      panel.hidden = panel !== match;
    }
  }

  /**
   * Creates one `<button role="tab">` per panel, reusing the ids the panel
   * already declares so `aria-labelledby` / `aria-controls` stay paired. The
   * initially visible panel (the one the section rendered without `hidden`)
   * becomes the selected tab, so the tabs match the server-rendered state.
   */
  #buildTabs() {
    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist) return;

    const panels = this.#panels();
    if (!panels.length) return;

    // Re-entrant safety: the theme editor can reconnect a section in place.
    tablist.replaceChildren();

    const fragment = document.createDocumentFragment();

    for (const panel of panels) {
      const label = panel.dataset.seasonLabel;
      if (!label) continue;

      const isSelected = !panel.hidden;
      const button = document.createElement('button');

      button.type = 'button';
      button.textContent = label;
      button.setAttribute('role', 'tab');
      button.setAttribute('ref', 'tabs[]');
      button.setAttribute('on:click', '/selectSeason');
      button.setAttribute('on:keydown', '/handleTabKeydown');
      button.setAttribute('aria-selected', String(isSelected));
      button.setAttribute('tabindex', isSelected ? '0' : '-1');
      button.setAttribute('aria-controls', panel.id);

      const tabId = panel.getAttribute('aria-labelledby');
      if (tabId) button.id = tabId;

      fragment.append(button);
    }

    tablist.append(fragment);
  }

  /**
   * Moves the roving tabindex to `index` and focuses that tab, without
   * changing which panel is selected.
   *
   * @param {number} index
   */
  #focusTab(index) {
    const tabs = this.#tabs();
    const nextTab = tabs[index];
    if (!nextTab) return;

    for (const [i, tab] of tabs.entries()) {
      tab.setAttribute('tabindex', i === index ? '0' : '-1');
    }

    nextTab.focus();
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const tabs = this.#tabs();
    const panels = this.#panels();
    const activePanel = panels[index];
    if (!tabs[index] || !activePanel) return;

    for (const [i, tab] of tabs.entries()) {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    activePanel.scrollLeft = 0;
  }
}

if (!customElements.get('seasonal-mosaic-component')) {
  customElements.define('seasonal-mosaic-component', SeasonalMosaicComponent);
}
